import React, { useState } from 'react';
import api from '../services/api';
import { GhostSelect } from '../components/ui/GhostSelect';

type Json = any;

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-black/30 backdrop-blur-md rounded-3xl border border-white/20 shadow-xl p-6 mb-6">
    <h2 className="text-xl font-semibold text-white mb-6 border-b border-white/10 pb-4 mt-0">{title}</h2>
    {children}
  </div>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex gap-4 items-center mb-4">
    <label className="w-48 text-white/70 text-sm font-medium">{label}</label>
    <div className="flex-1">{children}</div>
  </div>
);

const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input {...props} className={`w-full bg-black/20 border border-white/20 text-white text-sm rounded-xl focus:ring-blue-400 focus:border-blue-400 block p-2.5 placeholder-white/40 shadow-inner transition-all duration-300 hover:bg-black/75 hover:shadow-inner-glow focus:bg-black/60 focus:shadow-inner-glow ${props.className || ''}`} style={props.style} />
);

const TextArea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = (props) => (
  <textarea {...props} className={`w-full bg-black/20 border border-white/20 text-white text-sm rounded-xl focus:ring-blue-400 focus:border-blue-400 block p-2.5 placeholder-white/40 shadow-inner transition-all duration-300 hover:bg-black/75 hover:shadow-inner-glow focus:bg-black/60 focus:shadow-inner-glow min-h-[80px] ${props.className || ''}`} style={props.style} />
);

const Btn: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = (props) => (
  <button
    {...props}
    className={`btn-taratic px-4 py-2 bg-black/75 border border-white/20 shadow-lg text-white hover:bg-black/60 hover:shadow-inner-glow hover:scale-105 transition-all rounded-xl disabled:opacity-50 disabled:cursor-not-allowed ${props.className || ''}`}
    style={props.style}
  />
);

const TestApiPage: React.FC = () => {
  // Datos a rellenar (inputs globales)
  const [comercioKey, setComercioKey] = useState<string>(localStorage.getItem('comercio_key') || '');
  const [pageId, setPageId] = useState<string>('');
  const [igUserId, setIgUserId] = useState<string>('');
  const [threadId, setThreadId] = useState<string>('');
  const [recipientId, setRecipientId] = useState<string>(''); // IG customer id
  const [senderId, setSenderId] = useState<string>('');       // usually page_id or ig_user_id
  const [messageText, setMessageText] = useState<string>('Hola de prueba desde Test API');
  const [imageUrl, setImageUrl] = useState<string>('');
  const [pinnedOrder, setPinnedOrder] = useState<number>(0);
  const [timestampIso, setTimestampIso] = useState<string>(new Date().toISOString());

  // Chat Bots (generales) inputs
  const [botId, setBotId] = useState<string>('');
  const [botName, setBotName] = useState<string>('');
  const [botDescription, setBotDescription] = useState<string>('');
  const [botType, setBotType] = useState<string>('general');
  const [botChannelsCsv, setBotChannelsCsv] = useState<string>('instagram');

  // Outputs
  const [resp, setResp] = useState<string>('');
  const [err, setErr] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  // Expected vs Obtained (comparativa)
  const [expectedTitle, setExpectedTitle] = useState<string>('');
  const [expectedStatus, setExpectedStatus] = useState<number>(200);
  const [expectedBody, setExpectedBody] = useState<string>('');
  const [actualStatus, setActualStatus] = useState<number | null>(null);

  const setResponse = (data: Json) => {
    try {
      setResp(JSON.stringify(data, null, 2));
      setErr('');
    } catch {
      setResp(String(data));
      setErr('');
    }
  };
  const setError = (e: any) => {
    const msg = e?.response?.data?.message || e?.message || String(e);
    setErr(msg);
    try {
      const data = e?.response?.data;
      if (data) setResp(JSON.stringify(data, null, 2));
    } catch { }
  };

  const stringify = (v: any) => {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    try { return JSON.stringify(v, null, 2); } catch { return String(v); }
  };

  // Run helper with expected vs obtained comparison
  const run = async (
    title: string,
    expected: { status: number; body: any },
    call: () => Promise<import('axios').AxiosResponse<any>>
  ) => {
    setExpectedTitle(title);
    setExpectedStatus(expected.status);
    setExpectedBody(stringify(expected.body));
    setErr('');
    setResp('');
    setActualStatus(null);
    setLoading(true);
    try {
      const res = await call();
      setActualStatus(res?.status ?? 200);
      setResponse(res?.data);
    } catch (e: any) {
      setActualStatus(e?.response?.status ?? 0);
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  // Helpers
  const assertComercioKey = () => {
    if (!comercioKey) throw new Error('comercio_key requerido');
  };
  const assertThreadId = () => {
    if (!threadId) throw new Error('thread_id requerido');
  };
  const assertRecipientId = () => {
    if (!recipientId) throw new Error('recipient_id (IG customer) requerido');
  };

  // Sección: Pinned Threads (per-schema)
  const listPinned = async () => {
    assertComercioKey();
    return run(
      'GET /instagram/pinned-threads',
      { status: 200, body: { data: [] } },
      () => api.get(`/instagram/pinned-threads`, { params: { comercio_key: comercioKey, ts: Date.now() } })
    );
  };

  const pinThread = async () => {
    assertComercioKey();
    assertThreadId();
    return run(
      'POST /instagram/pinned-threads',
      { status: 200, body: { thread_id: '<THREAD_ID>', pinned_order: 0, pinned_at: '<ISO>' } },
      () => api.post(`/instagram/pinned-threads`, {
        comercio_key: comercioKey,
        thread_id: threadId,
        pinned_order: Number.isFinite(pinnedOrder) ? pinnedOrder : 0
      })
    );
  };

  const unpinThread = async () => {
    assertComercioKey();
    assertThreadId();
    return run(
      'DELETE /instagram/pinned-threads/:threadId',
      { status: 200, body: { success: true } },
      () => api.delete(`/instagram/pinned-threads/${encodeURIComponent(threadId)}`, { params: { comercio_key: comercioKey } })
    );
  };

  // Sección: Threads / Messages
  const listThreads = async () => {
    assertComercioKey();
    return run(
      'GET /instagram/threads',
      { status: 200, body: { data: [] } },
      () => api.get(`/instagram/threads`, { params: { comercio_key: comercioKey, ts: Date.now() } })
    );
  };

  const listMessages = async () => {
    assertComercioKey();
    assertThreadId();
    return run(
      'GET /instagram/threads/:threadId/messages',
      { status: 200, body: { data: [] } },
      () => api.get(`/instagram/threads/${encodeURIComponent(threadId)}/messages`, {
        params: { comercio_key: comercioKey, ts: Date.now() }
      })
    );
  };

  // Sección: Send message (UI)
  const sendMessageUI = async () => {
    assertComercioKey();
    assertThreadId();
    if (!messageText && !imageUrl) throw new Error('Proporciona messageText o imageUrl');
    return run(
      'POST /instagram/threads/:threadId/messages',
      { status: 201, body: { message: 'sent', data: { id: '<id>', fromMe: true } } },
      () => api.post(`/instagram/threads/${encodeURIComponent(threadId)}/messages`, {
        text: messageText || undefined,
        imageUrl: imageUrl || undefined,
        senderKind: 'human',
        origin: 'web'
      })
    );
  };

  // Sección: Send with metadata (n8n)
  const sendWithMetadata = async () => {
    assertComercioKey();
    assertThreadId();
    assertRecipientId();
    if (!messageText && !imageUrl) throw new Error('Proporciona messageText o imageUrl');
    return run(
      'POST /instagram/send-message-with-metadata',
      { status: 200, body: { success: true, message: 'Message sent with metadata' } },
      () => api.post(`/instagram/send-message-with-metadata`, {
        comercio_key: comercioKey,
        thread_id: threadId,
        recipient_id: recipientId,
        message_text: messageText || undefined,
        image_url: imageUrl || undefined,
        sender_kind: 'bot',
        origin: 'taratic_api',
        bot_name: 'test-bot',
        metadata: { test: true }
      })
    );
  };

  // Sección: Notify inbound (n8n -> UI)
  const notifyInbound = async () => {
    assertComercioKey();
    assertThreadId();
    if (!senderId) throw new Error('sender_id requerido');
    if (!messageText) throw new Error('message_text requerido');
    return run(
      'POST /instagram/notify-message',
      { status: 200, body: { success: true, message: 'Message notification received' } },
      () => api.post(`/instagram/notify-message`, {
        comercio_key: comercioKey,
        thread_id: threadId,
        sender_id: senderId,
        recipient_id: recipientId || undefined,
        message_text: messageText,
        message_type: 'text',
        attachments: [],
        timestamp: timestampIso || new Date().toISOString()
      })
    );
  };

  // Sección: Should auto-reply
  const shouldAutoReply = async () => {
    if (!messageText) throw new Error('message_text requerido para evaluar auto-reply');
    const body: any = {
      message_text: messageText,
      now_iso: timestampIso || new Date().toISOString()
    };
    if (comercioKey) body.comercio_key = comercioKey;
    if (threadId) body.thread_id = threadId;
    if (recipientId) body.customer_psid = recipientId;
    if (!comercioKey && (pageId || igUserId)) {
      body.page_id = pageId || undefined;
      body.ig_user_id = igUserId || undefined;
    }
    return run(
      'POST /instagram/should-auto-reply',
      { status: 200, body: { should_reply: true, config: { bot_enabled: true, bot_type: 'general' } } },
      () => api.post(`/instagram/should-auto-reply`, body)
    );
  };

  // One-call decision via connection-data by PAGE_ID
  const connectionDataByPageOneCall = async () => {
    if (!pageId) throw new Error('PAGE_ID requerido');
    if (!messageText) throw new Error('message_text requerido');
    return run(
      'GET /instagram/connection-data/by-page/:pageId (one-call BOT_DECISION)',
      { status: 200, body: { COMERCIO_KEY: comercioKey || '<schema>', THREAD_ID: '<THREAD_ID>', BOT_DECISION: { should_reply: true } } },
      () =>
        api.get(`/instagram/connection-data/by-page/${encodeURIComponent(pageId)}`, {
          params: {
            sender_id: senderId || undefined,
            recipient_id: recipientId || undefined,
            message_text: messageText,
            timestamp: timestampIso || new Date().toISOString()
          }
        })
    );
  };

  // One-call decision via connection-data by IG_USER_ID
  const connectionDataByIgUserIdOneCall = async () => {
    if (!igUserId) throw new Error('IG_USER_ID requerido');
    if (!messageText) throw new Error('message_text requerido');
    return run(
      'GET /instagram/connection-data/:igUserId (one-call BOT_DECISION)',
      { status: 200, body: { COMERCIO_KEY: comercioKey || '<schema>', THREAD_ID: '<THREAD_ID>', BOT_DECISION: { should_reply: true } } },
      () =>
        api.get(`/instagram/connection-data/${encodeURIComponent(igUserId)}`, {
          params: {
            sender_id: senderId || undefined,
            recipient_id: recipientId || undefined,
            message_text: messageText,
            timestamp: timestampIso || new Date().toISOString()
          }
        })
    );
  };

  // One-call decision via POST JSON (supports page_id or ig_user_id)
  const connectionDataOneCallJson = async () => {
    if (!igUserId && !pageId) throw new Error('IG_USER_ID o PAGE_ID requerido (al menos uno)');
    if (!messageText) throw new Error('message_text requerido');
    const body: any = {
      message_text: messageText,
      timestamp: timestampIso || new Date().toISOString(),
      sender_id: senderId || undefined,
      recipient_id: recipientId || undefined
    };
    if (igUserId) body.ig_user_id = igUserId;
    if (pageId) body.page_id = pageId;

    return run(
      'POST /instagram/connection-data/one-call (JSON)',
      { status: 200, body: { COMERCIO_KEY: comercioKey || '<schema>', THREAD_ID: '<THREAD_ID>', BOT_DECISION: { should_reply: true } } },
      () => api.post(`/instagram/connection-data/one-call`, body)
    );
  };

  // Sección: Get thread id by participants
  const getThreadId = async () => {
    assertComercioKey();
    if (!senderId && !recipientId) throw new Error('sender_id o recipient_id requerido');
    const body: any = {
      comercio_key: comercioKey,
      sender_id: senderId || undefined,
      recipient_id: recipientId || undefined,
      page_id: pageId || undefined,
      ig_user_id: igUserId || undefined
    };
    await run(
      'POST /instagram/get-thread-id',
      { status: 200, body: { success: true, thread_id: '<THREAD_ID>', customer_id: '<IG_CUSTOMER_ID>' } },
      () => api.post(`/instagram/get-thread-id`, body)
    );
    // run already sets response; fetch again from last resp string to patch local state if possible
    try {
      const parsed = JSON.parse(resp || '{}');
      if (parsed?.thread_id) setThreadId(parsed.thread_id);
      if (parsed?.customer_id && !recipientId) setRecipientId(parsed.customer_id);
    } catch { }
  };

  // Sección: Connection status
  const connectionStatus = async () => {
    assertComercioKey();
    return run(
      'GET /instagram/connection-status',
      { status: 200, body: { connected: true, comercio_key: comercioKey } },
      () => api.get(`/instagram/connection-status`, { params: { comercio_key: comercioKey } })
    );
  };

  // ===================== Chat Bots (generales) =====================
  const listBots = async () => {
    assertComercioKey();
    return run(
      'GET /chat/bots',
      { status: 200, body: { data: [] } },
      () => api.get(`/chat/bots`, { params: { comercio_key: comercioKey, ts: Date.now() } })
    );
  };

  const getActiveBot = async () => {
    assertComercioKey();
    return run(
      'GET /chat/bots/active',
      { status: 200, body: null },
      () => api.get(`/chat/bots/active`, { params: { comercio_key: comercioKey, ts: Date.now() } })
    );
  };

  const createBot = async () => {
    assertComercioKey();
    if (!botName.trim()) throw new Error('BOT_NAME requerido');
    const channels = (botChannelsCsv || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    return run(
      'POST /chat/bots',
      { status: 201, body: { id: '<id>', name: botName } },
      () => api.post(`/chat/bots`, {
        comercio_key: comercioKey,
        name: botName,
        description: botDescription || '',
        bot_type: botType || 'general',
        channels,
        is_active: false
      })
    );
  };

  const activateBot = async () => {
    assertComercioKey();
    if (!botId.trim()) throw new Error('BOT_ID requerido');
    return run(
      'POST /chat/bots/:id/activate',
      { status: 200, body: { is_active: true } },
      () => api.post(`/chat/bots/${encodeURIComponent(botId)}/activate`, { comercio_key: comercioKey })
    );
  };

  const deleteBot = async () => {
    assertComercioKey();
    if (!botId.trim()) throw new Error('BOT_ID requerido');
    return run(
      'DELETE /chat/bots/:id',
      { status: 200, body: { success: true } },
      () => api.delete(`/chat/bots/${encodeURIComponent(botId)}`, { params: { comercio_key: comercioKey } })
    );
  };

  // Helper: copiar comercio_key al localStorage si lo cambian aquí
  const persistComercioKey = () => {
    if (comercioKey) {
      localStorage.setItem('comercio_key', comercioKey);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto p-8 animate-fade-in-down overflow-x-hidden">
      <h1 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">Test API (Instagram)</h1>

      {/* Checklist enumerada para pruebas rápidas */}
      <Section title="Numbered Checklist (click to execute each case)">
        <ol className="pl-5 grid gap-3">
          <li>
            <div className="flex gap-2 items-center flex-wrap">
              <span>1) Connection Status — verifica conexión IG para el comercio</span>
              <Btn disabled={loading} onClick={connectionStatus}>Ejecutar</Btn>
              <small className="text-white/50 text-xs">(Requiere comercio_key)</small>
            </div>
          </li>
          <li>
            <div className="flex gap-2 items-center flex-wrap">
              <span>2) List Threads — lista de hilos recientes</span>
              <Btn disabled={loading} onClick={listThreads}>Ejecutar</Btn>
              <small className="text-white/50 text-xs">(Requiere comercio_key)</small>
            </div>
          </li>
          <li>
            <div className="flex gap-2 items-center flex-wrap">
              <span>3) List Messages — mensajes del THREAD_ID</span>
              <Btn disabled={loading} onClick={listMessages}>Ejecutar</Btn>
              <small className="text-white/50 text-xs">(Requiere comercio_key y THREAD_ID)</small>
            </div>
          </li>
          <li>
            <div className="flex gap-2 items-center flex-wrap">
              <span>4) Listar Pinned Threads — por comercio (per-schema)</span>
              <Btn disabled={loading} onClick={listPinned}>Ejecutar</Btn>
              <small className="text-white/50 text-xs">(Requiere comercio_key)</small>
            </div>
          </li>
          <li>
            <div className="flex gap-2 items-center flex-wrap">
              <span>5) Pin (UPSERT) — fija un hilo con pinned_order</span>
              <Btn disabled={loading} onClick={pinThread}>Ejecutar</Btn>
              <small className="text-white/50 text-xs">(Requiere comercio_key, THREAD_ID, pinned_order)</small>
            </div>
          </li>
          <li>
            <div className="flex gap-2 items-center flex-wrap">
              <span>6) Unpin — elimina el pin del hilo</span>
              <Btn disabled={loading} onClick={unpinThread}>Ejecutar</Btn>
              <small className="text-white/50 text-xs">(Requiere comercio_key y THREAD_ID)</small>
            </div>
          </li>
          <li>
            <div className="flex gap-2 items-center flex-wrap">
              <span>7) Send (UI) — enviar como humano desde la UI</span>
              <Btn disabled={loading} onClick={sendMessageUI}>Ejecutar</Btn>
              <small className="text-white/50 text-xs">(Requiere THREAD_ID y message_text o image_url)</small>
            </div>
          </li>
          <li>
            <div className="flex gap-2 items-center flex-wrap">
              <span>8) Send with metadata (n8n) — enviar como bot</span>
              <Btn disabled={loading} onClick={sendWithMetadata}>Ejecutar</Btn>
              <small className="text-white/50 text-xs">(Requiere comercio_key, THREAD_ID, RECIPIENT_ID, message_text o image_url)</small>
            </div>
          </li>
          <li>
            <div className="flex gap-2 items-center flex-wrap">
              <span>9) Notify inbound (n8n → UI) — simula mensaje entrante</span>
              <Btn disabled={loading} onClick={notifyInbound}>Ejecutar</Btn>
              <small className="text-white/50 text-xs">(Requiere comercio_key, THREAD_ID, SENDER_ID (cliente), message_text)</small>
            </div>
          </li>
          <li>
            <div className="flex gap-2 items-center flex-wrap">
              <span>10) Should auto-reply — decide si el bot debe responder</span>
              <Btn disabled={loading} onClick={shouldAutoReply}>Ejecutar</Btn>
              <small className="text-white/50 text-xs">(Requiere message_text y comercio_key o (page_id/ig_user_id))</small>
            </div>
          </li>
          <li>
            <div className="flex gap-2 items-center flex-wrap">
              <span>11) Get Thread ID — por participantes (resuelve thread_id)</span>
              <Btn disabled={loading} onClick={getThreadId}>Ejecutar</Btn>
              <small className="text-white/50 text-xs">(Requiere comercio_key y sender_id o recipient_id; opcional page_id/ig_user_id)</small>
            </div>
          </li>
        </ol>
      </Section>

      {/* Datos a rellenar (inputs globales) */}
      <Section title="Test Data (fill in here)">
        <Row label="comercio_key">
          <Input
            value={comercioKey}
            onChange={(e) => setComercioKey(e.target.value)}
            placeholder='p.ej. "deviluppercase_tienda"'
            onBlur={persistComercioKey}
          />
        </Row>
        <Row label="PAGE_ID (opcional)">
          <Input value={pageId} onChange={(e) => setPageId(e.target.value)} placeholder="page_id de la page de Facebook" />
        </Row>
        <Row label="IG_USER_ID (opcional)">
          <Input value={igUserId} onChange={(e) => setIgUserId(e.target.value)} placeholder="ig_user_id del comercio" />
        </Row>
        <Row label="THREAD_ID">
          <Input value={threadId} onChange={(e) => setThreadId(e.target.value)} placeholder="t_xxx o id real de la conversación" />
        </Row>
        <Row label="RECIPIENT_ID (cliente IG)">
          <Input value={recipientId} onChange={(e) => setRecipientId(e.target.value)} placeholder="instagram psid del cliente" />
        </Row>
        <Row label="SENDER_ID (quién envía)">
          <Input value={senderId} onChange={(e) => setSenderId(e.target.value)} placeholder="page_id o ig_user_id" />
        </Row>
        <Row label="message_text">
          <TextArea value={messageText} onChange={(e) => setMessageText(e.target.value)} placeholder="texto del mensaje" />
        </Row>
        <Row label="image_url (opcional)">
          <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="url de imagen" />
        </Row>
        <Row label="pinned_order">
          <Input type="number" value={String(pinnedOrder)} onChange={(e) => setPinnedOrder(parseInt(e.target.value || '0', 10))} />
        </Row>
        <Row label="timestamp ISO">
          <Input value={timestampIso} onChange={(e) => setTimestampIso(e.target.value)} placeholder={new Date().toISOString()} />
        </Row>

        {/* Chuleta SQL: PAGE_ID / IG_USER_ID */}
        <details className="mt-4 bg-black/20 border border-white/10 rounded-xl p-4 transition-all">
          <summary className="cursor-pointer text-blue-400 font-medium hover:text-blue-300 transition-colors">Ver chuleta SQL para PAGE_ID e IG_USER_ID</summary>
          <pre className="bg-black/50 p-4 rounded-xl overflow-x-auto text-sm text-green-400 border border-white/10 mt-3 font-mono">{`-- Reemplaza 'deviluppercase_tienda' por tu schema (comercio_key)
-- 1) Obtener PAGE_ID e IG_USER_ID por comercio_key
SELECT page_id AS "PAGE_ID", ig_user_id AS "IG_USER_ID"
FROM _admin_global.instagram_connections
WHERE comercio_key = 'deviluppercase_tienda'
ORDER BY connected_at DESC
LIMIT 1;

-- 2) Listado rápido de todos
SELECT comercio_key, page_id, ig_user_id
FROM _admin_global.instagram_connections
ORDER BY comercio_key;`}</pre>
        </details>
      </Section>

      {/* Pinned threads */}
      <Section title="Pinned Threads per commerce (per-schema)">
        <div className="flex gap-2 flex-wrap mb-4">
          <Btn disabled={loading} onClick={listPinned}>Listar pinned</Btn>
          <Btn disabled={loading} onClick={pinThread}>Pin (UPSERT)</Btn>
          <Btn disabled={loading} onClick={unpinThread}>Unpin</Btn>
        </div>
        <p className="mt-4 text-white/50 text-sm">
          Endpoints: GET/POST/DELETE pinned-threads — ver [router.get()](backend/src/routes/instagram.js:1116), [router.post()](backend/src/routes/instagram.js:1131), [router.delete()](backend/src/routes/instagram.js:1150)
        </p>
        {/* Chuleta SQL: comprobar pins por schema */}
        <details className="mt-4 bg-black/20 border border-white/10 rounded-xl p-4 transition-all">
          <summary className="cursor-pointer text-blue-400 font-medium hover:text-blue-300 transition-colors">Ver chuleta SQL para comprobar pinned threads</summary>
          <pre className="bg-black/50 p-4 rounded-xl overflow-x-auto text-sm text-green-400 border border-white/10 mt-3 font-mono">{`-- Listar pins por comercio_key
SELECT thread_id, pinned_order, pinned_at
FROM "deviluppercase_tienda".instagram_pinned_threads
ORDER BY pinned_order, pinned_at;

-- Repite en otro schema para validar aislamiento:
SELECT thread_id, pinned_order, pinned_at
FROM "taratic_demo_uno".instagram_pinned_threads
ORDER BY pinned_order, pinned_at;`}</pre>
        </details>
      </Section>

      {/* Chat Bots generales (canal-agnóstico) */}
      <Section title="General Chat Bots (channel-agnostic)">
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
          <Btn disabled={loading} onClick={listBots}>Listar bots</Btn>
          <Btn disabled={loading} onClick={getActiveBot}>Obtener bot activo</Btn>
          <Btn disabled={loading} onClick={createBot} className="btn-taratic btn-blue">Crear bot</Btn>
          <Btn disabled={loading} onClick={activateBot}>Activar bot</Btn>
          <Btn disabled={loading} onClick={deleteBot}>Eliminar bot</Btn>
        </div>

        {/* Inputs específicos para Chat Bots */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          <div>
            <Row label="BOT_ID (para activar/eliminar)">
              <Input value={botId} onChange={(e) => setBotId(e.target.value)} placeholder="id del bot" />
            </Row>
            <Row label="BOT_NAME">
              <Input value={botName} onChange={(e) => setBotName(e.target.value)} placeholder='p.ej. "General 2"' />
            </Row>
            <Row label="BOT_DESCRIPTION">
              <Input value={botDescription} onChange={(e) => setBotDescription(e.target.value)} placeholder="Descripción" />
            </Row>
          </div>
          <div>
            <Row label="BOT_TYPE">
              <GhostSelect
                options={[
                  { value: "general", label: "general" },
                  { value: "ventas", label: "ventas" },
                  { value: "soporte", label: "soporte" },
                  { value: "reservas", label: "reservas" }
                ]}
                value={botType}
                onChange={(val: string) => setBotType(val)}
                className="w-full"
              />
            </Row>
            <Row label="BOT_CHANNELS (CSV)">
              <Input value={botChannelsCsv} onChange={(e) => setBotChannelsCsv(e.target.value)} placeholder="instagram,whatsapp,email" />
            </Row>
          </div>
        </div>

        <p style={{ marginTop: '0.5rem', color: '#666' }}>
          Endpoints: [router.get()](backend/src/routes/chat.js:23), [router.get()](backend/src/routes/chat.js:43), [router.post()](backend/src/routes/chat.js:66), [router.post()](backend/src/routes/chat.js:198), [router.delete()](backend/src/routes/chat.js:238). Tabla por comercio: chat_bots (creada por SQL).
        </p>
        {/* Chuleta SQL Chat Bots */}
        <details className="mt-4 bg-black/20 border border-white/10 rounded-xl p-4 transition-all">
          <summary className="cursor-pointer text-blue-400 font-medium hover:text-blue-300 transition-colors">Ver chuleta SQL para Chat Bots</summary>
          <pre className="bg-black/50 p-4 rounded-xl overflow-x-auto text-sm text-green-400 border border-white/10 mt-3 font-mono">{`-- Ver bots por comercio
SELECT id, name, bot_type, is_active, channels
FROM "deviluppercase_tienda".chat_bots
ORDER BY is_active DESC, id;

-- Activar uno manualmente (como referencia)
UPDATE "deviluppercase_tienda".chat_bots SET is_active = (id = 3);

-- Constraint (único activo): ver índice parcial creado por script
-- uq_deviluppercase_tienda_chat_bots_only_one_active (is_active) WHERE is_active = TRUE`}</pre>
        </details>
      </Section>

      {/* Threads/Messages */}
      <Section title="Threads and Messages">
        <div className="flex gap-2 flex-wrap mb-4">
          <Btn disabled={loading} onClick={listThreads}>Listar threads</Btn>
          <Btn disabled={loading} onClick={listMessages}>Listar mensajes del THREAD_ID</Btn>
        </div>
        <p style={{ marginTop: '0.5rem', color: '#666' }}>
          Ver [router.get()](backend/src/routes/instagram.js:269) y [router.get()](backend/src/routes/instagram.js:323)
        </p>
        {/* Chuleta SQL: obtener THREAD_ID recientes */}
        <details className="mt-4 bg-black/20 border border-white/10 rounded-xl p-4 transition-all">
          <summary className="cursor-pointer text-blue-400 font-medium hover:text-blue-300 transition-colors">Ver chuleta SQL para obtener THREAD_ID</summary>
          <pre className="bg-black/50 p-4 rounded-xl overflow-x-auto text-sm text-green-400 border border-white/10 mt-3 font-mono">{`-- A) THREAD_ID más recientes desde chat_sessions
SELECT thread_id, last_message_at, message_count
FROM "deviluppercase_tienda".chat_sessions
ORDER BY last_message_at DESC NULLS LAST
LIMIT 20;

-- B) THREAD_ID más recientes desde chat_message_metadata (canal IG)
SELECT thread_id,
       MAX(COALESCE(sent_at, created_at)) AS last_at,
       COUNT(*) AS cnt
FROM "deviluppercase_tienda".chat_message_metadata
WHERE channel = 'instagram'
GROUP BY thread_id
ORDER BY last_at DESC
LIMIT 20;

-- C) Fallback histórico IG (si existe tabla antigua)
SELECT thread_id,
       MAX(sent_at) AS last_at,
       COUNT(*)     AS cnt
FROM "deviluppercase_tienda".instagram_message_metadata
GROUP BY thread_id
ORDER BY last_at DESC
LIMIT 20;`}</pre>
        </details>
      </Section>

      {/* Messaging */}
      <Section title="Messaging (UI / n8n)">
        <div className="flex gap-2 flex-wrap mb-4">
          <Btn disabled={loading} onClick={sendMessageUI}>Send (UI)</Btn>
          <Btn disabled={loading} onClick={sendWithMetadata}>Send with metadata (n8n)</Btn>
          <Btn disabled={loading} onClick={notifyInbound}>Notify inbound (n8n → UI)</Btn>
        </div>
        <p style={{ marginTop: '0.5rem', color: '#666' }}>
          Endpoints: [router.post()](backend/src/routes/instagram.js:434), [router.post()](backend/src/routes/instagram.js:1382), [router.post()](backend/src/routes/instagram.js:733)
        </p>
        {/* Chuleta SQL: RECIPIENT_ID (cliente) y SENDER_ID (negocio) */}
        <details className="mt-4 bg-black/20 border border-white/10 rounded-xl p-4 transition-all">
          <summary className="cursor-pointer text-blue-400 font-medium hover:text-blue-300 transition-colors">Ver chuleta SQL para RECIPIENT_ID y SENDER_ID</summary>
          <pre className="bg-black/50 p-4 rounded-xl overflow-x-auto text-sm text-green-400 border border-white/10 mt-3 font-mono">{`-- RECIPIENT_ID (cliente IG) por THREAD_ID
-- Prioridad 1: desde chat_sessions
SELECT customer_id
FROM "deviluppercase_tienda".chat_sessions
WHERE thread_id = '<THREAD_ID>'
LIMIT 1;

-- Prioridad 2: desde inbound en chat_message_metadata (metadata->>'sender_id')
SELECT metadata->>'sender_id' AS customer_id
FROM "deviluppercase_tienda".chat_message_metadata
WHERE channel = 'instagram'
  AND thread_id = '<THREAD_ID>'
  AND origin = 'instagram'
  AND sender_kind = 'human'
  AND (metadata ? 'sender_id')
ORDER BY sent_at DESC NULLS LAST, created_at DESC
LIMIT 1;

-- SENDER_ID (negocio) para outbound: usa PAGE_ID o IG_USER_ID del comercio
SELECT page_id, ig_user_id
FROM _admin_global.instagram_connections
WHERE comercio_key = 'deviluppercase_tienda'
ORDER BY connected_at DESC
LIMIT 1;

-- Consulta combinada rápida (último thread + cliente + IDs de negocio)
WITH latest AS (
  SELECT thread_id,
         MAX(COALESCE(sent_at, created_at)) AS last_at
  FROM "deviluppercase_tienda".chat_message_metadata
  WHERE channel = 'instagram'
  GROUP BY thread_id
  ORDER BY last_at DESC
  LIMIT 1
)
SELECT
  l.thread_id                                  AS "THREAD_ID",
  s.customer_id                                AS "RECIPIENT_ID_from_sessions",
  (
    SELECT m.metadata->>'sender_id'
    FROM "deviluppercase_tienda".chat_message_metadata m
    WHERE m.channel='instagram'
      AND m.thread_id = l.thread_id
      AND m.origin='instagram'
      AND m.sender_kind='human'
      AND (m.metadata ? 'sender_id')
    ORDER BY m.sent_at DESC NULLS LAST, m.created_at DESC
    LIMIT 1
  )                                            AS "RECIPIENT_ID_from_inbound_metadata",
  c.page_id                                    AS "PAGE_ID",
  c.ig_user_id                                 AS "IG_USER_ID"
FROM latest l
LEFT JOIN "deviluppercase_tienda".chat_sessions s ON s.thread_id = l.thread_id
LEFT JOIN _admin_global.instagram_connections c   ON c.comercio_key = 'deviluppercase_tienda'
ORDER BY c.connected_at DESC
LIMIT 1;`}</pre>
        </details>
      </Section>

      {/* Should auto-reply */}
      <Section title="Should Auto-Reply">
        <div className="flex gap-2 flex-wrap mb-4">
          <Btn disabled={loading} onClick={shouldAutoReply}>Evaluar should-auto-reply</Btn>
        </div>
        <p style={{ marginTop: '0.5rem', color: '#666' }}>Endpoint: [router.post()](backend/src/routes/instagram.js:951)</p>
      </Section>

      {/* Connection-Data (one-call BOT_DECISION) */}
      <Section title="Connection-Data (one-call BOT_DECISION)">
        <div className="flex gap-2 flex-wrap mb-4">
          <Btn disabled={loading} onClick={connectionDataByPageOneCall}>by PAGE_ID (GET)</Btn>
          <Btn disabled={loading} onClick={connectionDataByIgUserIdOneCall}>by IG_USER_ID (GET)</Btn>
          <Btn disabled={loading} onClick={connectionDataOneCallJson}>One-Call (POST JSON)</Btn>
        </div>
        <p style={{ marginTop: '0.5rem', color: '#666' }}>
          Endpoints: [router.get()](backend/src/routes/instagram.js:1156), [router.get()](backend/src/routes/instagram.js:562), [router.post()](backend/src/routes/instagram.js:1348)
        </p>
      </Section>

      {/* Get thread id */}
      <Section title="Get Thread ID by participants">
        <div className="flex gap-2 flex-wrap mb-4">
          <Btn disabled={loading} onClick={getThreadId}>Resolver/obtener THREAD_ID</Btn>
        </div>
        <p style={{ marginTop: '0.5rem', color: '#666' }}>Endpoint: [router.post()](backend/src/routes/instagram.js:1176)</p>
        {/* Chuleta SQL: soporte al get-thread-id (participantes) */}
        <details className="mt-4 bg-black/20 border border-white/10 rounded-xl p-4 transition-all">
          <summary className="cursor-pointer text-blue-400 font-medium hover:text-blue-300 transition-colors">Ver chuleta SQL relacionada</summary>
          <pre className="bg-black/50 p-4 rounded-xl overflow-x-auto text-sm text-green-400 border border-white/10 mt-3 font-mono">{`-- Para obtener candidate THREAD_ID por actividad reciente sin llamar al endpoint:
SELECT thread_id
FROM "deviluppercase_tienda".chat_sessions
ORDER BY last_message_at DESC NULLS LAST
LIMIT 5;

-- Si no hay sesiones, usa chat_message_metadata para ver threads más activos:
SELECT thread_id,
       MAX(COALESCE(sent_at, created_at)) AS last_at
FROM "deviluppercase_tienda".chat_message_metadata
WHERE channel = 'instagram'
GROUP BY thread_id
ORDER BY last_at DESC
LIMIT 5;`}</pre>
        </details>
      </Section>

      {/* Connection status */}
      <Section title="Connection Status">
        <div className="flex gap-2 flex-wrap mb-4">
          <Btn disabled={loading} onClick={connectionStatus}>Consultar conexión</Btn>
        </div>
        <p style={{ marginTop: '0.5rem', color: '#666' }}>Endpoint: [router.get()](backend/src/routes/instagram.js:195)</p>
      </Section>

      {/* Output: Comparativa Esperado vs Obtenido */}
      <Section title="Comparison (Expected vs Obtained)">
        {loading && <div className="text-white/60 mb-2 animate-pulse">Loading...</div>}
        {err && (
          <div className="text-red-400 mb-2 bg-red-500/10 p-3 rounded-lg border border-red-500/20">
            <strong>Error:</strong> {err}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Columna Esperado */}
          <div className="border border-white/20 rounded-xl overflow-hidden bg-black/20 shadow-inner">
            <div className="px-3 py-2 bg-black/75 border-b border-white/20 text-white font-medium flex flex-wrap gap-2 items-center">
              <strong>Expected</strong> {expectedTitle ? <span className="text-white/70 font-normal text-sm">— {expectedTitle}</span> : ''}
            </div>
            <div className="p-3">
              <div className="mb-2 flex items-center gap-2">
                <span className="text-xs text-white/50 uppercase tracking-wider">Status</span>
                <div className="inline-block px-2 py-0.5 rounded-md bg-green-500/20 text-green-400 font-bold text-sm border border-green-500/30">
                  {expectedStatus}
                </div>
              </div>
              <pre className="bg-black/60 p-3 rounded-lg overflow-x-auto min-h-[120px] text-sm text-blue-300 border border-white/10 font-mono shadow-inner">
                {expectedBody || '// Define a case by executing a button to see the expected result'}
              </pre>
            </div>
          </div>
          {/* Columna Obtenido */}
          <div className="border border-white/20 rounded-xl overflow-hidden bg-black/20 shadow-inner">
            <div className="px-3 py-2 bg-black/75 border-b border-white/20 text-white font-medium">
              <strong>Obtained</strong>
            </div>
            <div className="p-3">
              <div className="mb-2 flex items-center gap-2">
                <span className="text-xs text-white/50 uppercase tracking-wider">Status</span>
                <div
                  className={`inline-block px-2 py-0.5 rounded-md font-bold text-sm border ${actualStatus == null
                    ? 'bg-white/10 text-white/70 border-white/20'
                    : actualStatus === expectedStatus
                      ? 'bg-green-500/20 text-green-400 border-green-500/30'
                      : 'bg-red-500/20 text-red-400 border-red-500/30'
                    }`}
                >
                  {actualStatus == null ? '-' : actualStatus}
                </div>
              </div>
              <pre className="bg-black/60 p-3 rounded-lg overflow-x-auto min-h-[120px] text-sm text-green-300 border border-white/10 font-mono shadow-inner">
                {resp || '// Execute an action to see the obtained result'}
              </pre>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default TestApiPage;
